import type { TokenResponseExtended as TokenResponse } from './tokenResponseExtended';
import type { MailboxInfo } from 'owa-client-types';
import type { HeadersWithoutIterator, RequestOptions } from './RequestOptions';
import { type AccountFeatureName, type GlobalFeatureName } from 'owa-feature-flags';
import type MailboxRequestOptions from './MailboxRequestOptions';
import type { TraceErrorObject } from 'owa-analytics-types';
export interface MetaOSContext {
    locale: string;
    hostName: string;
    clientType?: string;
    dateFormat?: string;
    timeFormat?: string;
    ring?: string;
}
import type { ApplicationSettingGroup, ApplicationSettings } from 'owa-application-settings';
import type { LicensingMailboxInfo, PolicyStateTypeEnum } from 'owa-account-source-list-types';
import type { NestedAppAuthPrefetchRequest } from 'owa-nested-app-auth-types';

/**
 * STRATIFY DEBT: This package is classified as utility because its request contracts,
 * configuration, and transport framework are foundational dependencies. It also owns generated
 * endpoint operations and service-specific request behavior that do not belong in a pure utility.
 *
 * Move those operations and service calls into service-layer packages while retaining only the
 * framework-neutral request primitives here.
 *
 * Tracking bug:
 * https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/456370
 */
export interface CircuitBreakerApi {
    shouldAllowRequest(backendKey: string): boolean;
    recordResult(
        backendKey: string,
        success: boolean,
        statusCode?: number,
        mailboxInfo?: MailboxInfo,
        backendTarget?: string
    ): void;
    recordAbort(
        backendKey: string,
        actionName: string,
        mailboxInfo?: MailboxInfo,
        backendTarget?: string
    ): void;
    recordRequestAllowed(backendKey: string): void;
    createCircuitOpenError(actionName?: string): TraceErrorObject;
}

export interface ServiceRequestConfig {
    getMsalToken?: (
        mailboxInfo: MailboxInfo,
        apiName: string,
        resource?: string,
        scope?: string,
        correlationId?: string,
        wwwAuthenticateHeader?: string,
        claims?: string,
        suppressInteractiveFallback?: boolean
    ) => Promise<TokenResponse | undefined>;
    getAuthToken?: (
        headers?: HeadersWithoutIterator,
        mailboxInfo?: MailboxInfo
    ) => Promise<string | undefined>;
    getResourceToken?: (
        resource: string,
        wwwAuthenticateHeader?: string,
        mailboxInfo?: MailboxInfo,
        scope?: string,
        childAppId?: string,
        allowPrompt?: boolean,
        claims?: string,
        requestId?: string,
        suppressInteractiveFallback?: boolean
    ) => Promise<TokenResponse | undefined>;
    getUserToken?: (
        headers?: HeadersWithoutIterator,
        mailboxInfo?: MailboxInfo
    ) => Promise<string | undefined>;
    getAuthTokenResponse?: (
        headers?: HeadersWithoutIterator,
        mailboxInfo?: MailboxInfo
    ) => Promise<TokenResponse | undefined>;
    getWebSessionType?: (mailboxInfo?: MailboxInfo) => Promise<string>;
    onAuthFailed?: (headers?: HeadersWithoutIterator, mailboxInfo?: MailboxInfo) => void;
    baseUrl: string;
    timezone?: string;
    isFeatureEnabled: (feature: GlobalFeatureName, mailboxInfo?: MailboxInfo) => boolean;
    isAccountFeatureEnabled: (feature: AccountFeatureName, mailboxInfo: MailboxInfo) => boolean;
    isUserIdle?: () => boolean;
    prepareRequestOptions?: (
        options: RequestOptions | MailboxRequestOptions | undefined
    ) => Promise<RequestOptions | MailboxRequestOptions | null | undefined>;
    timeoutMS?: number;
    disableAllRequests?: boolean;
    getAnchorMailbox?: (mailboxInfo?: MailboxInfo) => Promise<string | undefined>;
    getExplicitLogon?: (mailboxInfo?: MailboxInfo) => Promise<string | undefined>;
    isResourceTokenPrefetchEnabled?: boolean;
    metaOSContext?: MetaOSContext;
    getGlobalSettingsAccountMailboxInfo?: () => MailboxInfo | undefined;
    getGlobalSettingsAccountPersistenceId?: () => string | undefined;
    getCanaryHeaders?: () => Record<string, string>;
    executeNaaRequest?: (
        mailboxInfo: MailboxInfo,
        request: string,
        origin: string,
        isTrustedOrigin: boolean,
        targetWindow: Window,
        scenarioName: string,
        callerId: string
    ) => Promise<string>;
    prefetchNaaRequest?: (
        mailboxInfo: MailboxInfo,
        request: NestedAppAuthPrefetchRequest,
        host: string,
        scenarioName: string
    ) => void;
    checkAndHandleHipException?: (
        headers?: HeadersWithoutIterator,
        mailboxInfo?: MailboxInfo
    ) => Promise<boolean>;
    checkAndHandleGATFRFailure?: (headers?: HeadersWithoutIterator) => void;
    checkAndHandleMSALFailure?: (tokenResponse?: TokenResponse) => void;
    onActivityTimeoutError?: () => Promise<void>;
    onPinnedAppsChanged?: (mailboxInfo: MailboxInfo, newSetting: string) => void;
    getModuleContextMailboxInfo?: () => Promise<MailboxInfo>;
    getApplicationSettings?: <TGroup extends ApplicationSettingGroup>(
        group: TGroup,
        mailboxInfo?: MailboxInfo,
        dontErrorIfNotInitialized?: boolean
    ) => ApplicationSettings[TGroup];
    getAudienceForCallsToOWS?: (requestUrl: string) => string | undefined;
    owsServerParamOverrideAllowed?: boolean;
    isMsalFlowEnabled?: boolean;
    // Used to determine if Nested App Auth (NAA) channel is recommended for the current app.
    isNAAChannelRecommended?: boolean;
    // Checks the account soure list store to try and get the policy state of the licensing mailbox
    getLicensingMailboxPolicyState?: (
        mailboxInfo: LicensingMailboxInfo
    ) => PolicyStateTypeEnum | undefined;
    onFederatedLogout?: (
        responseUrl: string,
        mailboxInfo: MailboxInfo,
        logoutSidHeader: string | null
    ) => Promise<boolean>;
    getTenantId: (mailboxInfo?: MailboxInfo) => string | undefined;
    signoutFromOwa?: (addABTSignoutQueryParam?: boolean, upn?: string) => Promise<void>;
    circuitBreaker?: CircuitBreakerApi;
}

const defaultCachedConfig: ServiceRequestConfig = {
    baseUrl: '/owa',
    isFeatureEnabled: () => false,
    isAccountFeatureEnabled: () => false,
    timeoutMS: 90000,
    disableAllRequests: false,
    isResourceTokenPrefetchEnabled: false,
    getTenantId: (_mailboxInfo?: MailboxInfo) => undefined,
};

let cachedConfig: ServiceRequestConfig = defaultCachedConfig;

export function updateServiceConfig(config?: Partial<ServiceRequestConfig>): void {
    cachedConfig = {
        ...cachedConfig,
        ...config,
        // Ensure getTenantId is always defined.
        getTenantId: (config && config.getTenantId) || cachedConfig.getTenantId,
    };
}

export function getConfig(): ServiceRequestConfig {
    return cachedConfig;
}

export function resetDefault(): void {
    cachedConfig = defaultCachedConfig;
}
